import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useToast } from "../components/userToast"; 
import { BASE_URL } from "../baseurl";

export default function ProductGrid() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const toast = useToast();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("default");
  const [currentPage, setCurrentPage] = useState(1);

  const PAGE_SIZE = 8;

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${BASE_URL}/items`);
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch items");
        }

        setProducts(data.items || []);
      } catch (err) {
        console.error("Fetch items error:", err);
        setError(err.message);
        toast({
          title: "Couldn't load products",
          description: err.message,
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    addToCart(product);
  };

  const goToProduct = (id) => {
    navigate(`/product/${id}`);
  };

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return a.price - b.price;
      case "price-high":
        return b.price - a.price;
      case "latest":
        return new Date(b.createdAt) - new Date(a.createdAt);
      default:
        return 0;
    }
  });

  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / PAGE_SIZE));
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageProducts = sortedProducts.slice(startIdx, startIdx + PAGE_SIZE);

  return (
    <section className="w-full bg-white py-12 px-6 md:px-16">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <p className="text-gray-500 text-sm">
            {loading
              ? "Loading products..."
              : `Showing ${sortedProducts.length === 0 ? 0 : startIdx + 1}–${Math.min(
                  startIdx + PAGE_SIZE,
                  sortedProducts.length
                )} of ${sortedProducts.length} results`}
          </p>

          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setCurrentPage(1);
            }}
            className="bg-gray-50 border border-gray-300 text-gray-700 text-sm rounded-md px-4 py-2 focus:outline-none focus:border-teal-500"
          >
            <option value="default">Default sorting</option>
            <option value="latest">Sort by latest</option>
            <option value="price-low">Sort by price: low to high</option>
            <option value="price-high">Sort by price: high to low</option>
          </select>
        </div>

        {error && !loading && (
          <div className="text-center py-16 text-gray-500">
            Something went wrong loading products. Please try again later.
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-100 rounded-lg w-full h-[260px]" />
                <div className="h-4 bg-gray-100 rounded mt-4 w-3/4 mx-auto" />
                <div className="h-4 bg-gray-100 rounded mt-2 w-1/3 mx-auto" />
              </div>
            ))}
          </div>
        )}

        {!loading && !error && pageProducts.length === 0 && (
          <div className="text-center py-16 text-gray-500">No products found.</div>
        )}

        {!loading && !error && pageProducts.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {pageProducts.map((product) => (
              <div
                key={product._id}
                onClick={() => goToProduct(product._id)}
                className="cursor-pointer group"
              >
                <div className="relative bg-gray-50 border border-gray-200 rounded-lg overflow-hidden w-full h-[260px] flex items-center justify-center">
                  {product.image ? (
                    <img
                      src={`${BASE_URL.replace("/api", "")}${product.image}`}
                      alt={product.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <svg
                      width="60"
                      height="60"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-gray-300"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
                      <circle cx="9" cy="9" r="1.5" fill="currentColor" />
                      <path
                        d="M21 15l-5-5-9 9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}

                  <button
                    onClick={(e) => handleAddToCart(e, product)}
                    className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white border border-gray-300 flex items-center justify-center text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-teal-500 hover:text-white hover:border-teal-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="826 837.5 140 121"
                    >
                      <path
                        fill="currentColor"
                        d="M878.77,943.611c0,2.75-1.005,5.131-3.015,7.141c-2.009,2.01-4.389,3.014-7.139,3.014c-2.75,0-5.13-1.004-7.139-3.014  c-2.01-2.01-3.015-4.391-3.015-7.141c0-2.749,1.005-5.129,3.015-7.138c2.009-2.011,4.389-3.016,7.139-3.016  c2.75,0,5.13,1.005,7.139,3.016C877.765,938.482,878.77,940.862,878.77,943.611z M949.846,943.611c0,2.75-1.005,5.131-3.015,7.141  s-4.39,3.014-7.141,3.014c-2.748,0-5.129-1.004-7.138-3.014c-2.01-2.01-3.015-4.391-3.015-7.141c0-2.749,1.005-5.129,3.015-7.138  c2.009-2.011,4.39-3.016,7.138-3.016c2.751,0,5.131,1.005,7.141,3.016C948.841,938.482,949.846,940.862,949.846,943.611z   M960,857.306v40.615c0,1.27-0.438,2.393-1.311,3.371s-1.943,1.548-3.212,1.705l-82.815,9.678c0.687,3.174,1.031,5.024,1.031,5.554  c0,0.846-0.635,2.539-1.904,5.076h72.979c1.375,0,2.564,0.503,3.569,1.508c1.006,1.005,1.508,2.194,1.508,3.569  c0,1.376-0.502,2.564-1.508,3.569c-1.005,1.005-2.194,1.507-3.569,1.507H863.54c-1.375,0-2.565-0.502-3.57-1.507  s-1.507-2.193-1.507-3.569c0-0.581,0.212-1.415,0.634-2.498c0.424-1.085,0.847-2.036,1.27-2.855c0.423-0.82,0.992-1.878,1.706-3.174  s1.124-2.076,1.23-2.34l-14.041-65.285h-16.183c-1.375,0-2.564-0.502-3.569-1.507c-1.005-1.005-1.508-2.195-1.508-3.57  c0-1.375,0.502-2.565,1.508-3.57c1.004-1.004,2.194-1.507,3.569-1.507h20.308c0.846,0,1.6,0.172,2.261,0.516  s1.177,0.754,1.547,1.229c0.37,0.476,0.714,1.124,1.032,1.944c0.316,0.819,0.528,1.507,0.634,2.062  c0.106,0.556,0.252,1.336,0.437,2.34c0.185,1.005,0.304,1.692,0.357,2.063h95.271c1.375,0,2.563,0.502,3.57,1.507  C959.497,854.741,960,855.931,960,857.306z"
                      />
                    </svg>
                  </button>
                </div>

                <h3 className="text-gray-800 font-medium text-[16px] mt-4 text-center">
                  {product.title}
                </h3>

                <p className="text-teal-600 text-[15px] mt-1 text-center">
                  ${Number(product.price).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-12">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-9 h-9 flex items-center justify-center rounded-md border text-sm transition-colors ${
                  currentPage === page
                    ? "bg-teal-500 text-white border-teal-500"
                    : "border-gray-300 text-gray-600 hover:border-teal-500 hover:text-teal-600"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:border-teal-500 hover:text-teal-600 transition-colors"
            >
              →
            </button>
          </div>
        )}
      </div>
    </section>
  );
}